import { BaseEnvConfigService, ClientApp } from '@ianesthesie-kit/config';
import { SupportLinkName, supportLinks } from './supportLinks';
import { differenceInCalendarDays, isValid } from 'date-fns';
import { Hospital, Region } from '@ianesthesie-kit/services';
import type { StorageAccess as StorageAccessType } from '@ianesthesie-kit/core';
import { PasswordConfig } from '@ianesthesie-kit/form';
import { UnitSystem } from '@/pages-client/questionnaire/unitConversion.service';

export class AppConfigService extends BaseEnvConfigService {
  private static readonly INSTANCE = new AppConfigService();
  private static readonly KEY_PHONE_VERIFICATION_PROMPT =
    'phoneVerificationModalDeclineTime';
  private static readonly PASS_CONFIG: PasswordConfig = {
    special: false,
    errorMessage: 'app.validation.passwordRequirements.patientApp'
  };

  private currentHospital?: Hospital;
  private storageAccess?: typeof StorageAccessType;

  private constructor() {
    super();

    if (typeof window !== 'undefined') {
      import('@ianesthesie-kit/core').then((m) => {
        this.storageAccess = m.StorageAccess;
      });
    }
  }

  public static get isDev() {
    return AppConfigService.getInstance().env === 'dev';
  }

  public static get isStg() {
    return AppConfigService.getInstance().env === 'stg';
  }

  public get gtmEnabled() {
    return !!this.gtmId;
  }

  public get gtmId() {
    return process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID || '';
  }

  public get fbPixelEnabled() {
    return !!this.fbPixelId;
  }

  public get fbPixelId() {
    return process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID || '';
  }

  public get intercomEnabled() {
    return !!this.intercomAppId;
  }

  public get intercomAppId() {
    return process.env.NEXT_PUBLIC_INTERCOM_APP_ID || '';
  }

  public get sentryEnabled() {
    return !!this.sentryDsn && this.env !== 'dev';
  }

  public get sentryDsn() {
    return process.env.NEXT_PUBLIC_SENTRY_DSN || '';
  }

  public get sentrySampleRate() {
    return this.env === 'stg' ? 1 : 0.5;
  }

  public get sentryReplaySessionSampleRate() {
    return 0; // never record without a reason
  }

  public get sentryReplayOnErrorSampleRate() {
    return this.env === 'stg' ? 0 : 1; // capture only prod errors
  }

  public get sentryDebugMode() {
    return this.env === 'stg';
  }

  public get getSentryIgnoreErrors() {
    return [/^Network Error$/, /^annulé$/, /^Cancel rendering route$/];
  }

  public get currentCountry() {
    if (this.env === 'produs') {
      return 'US';
    }

    if (this.env === 'prodfr') {
      return 'FR';
    }

    if (this.currentHospital?.addressDetails.region) {
      return this.currentHospital?.addressDetails.region === Region.FRANCE
        ? 'FR'
        : 'US';
    }

    return 'FR';
  }

  public get defaultUnitSystem(): UnitSystem {
    return this.currentCountry === 'US' ? 'imperial' : 'metric';
  }

  public get maidenNameEnabled(): boolean {
    return this.currentCountry === 'FR';
  }

  public get middleNameEnabled(): boolean {
    return this.currentCountry === 'US';
  }

  public get isResidenceRestricted(): boolean {
    return this.currentCountry === 'US';
  }

  public get riskMapping() {
    return {
      enabled: this.currentCountry === 'US'
    };
  }

  public get phoneVerificationPromptDisplayable() {
    // TODO temporarily override for US flow
    // FIXME remove when OTP is enabled in the US;
    if (this.currentCountry === 'US') {
      return false;
    }

    try {
      const value = this.storageAccess
        ?.getInstance()
        .getItem(AppConfigService.KEY_PHONE_VERIFICATION_PROMPT);
      if (!value) {
        return true;
      }
      const lastDeclineDate = new Date(+value);
      return (
        !isValid(lastDeclineDate) ||
        Math.abs(differenceInCalendarDays(lastDeclineDate, Date.now())) > 2
      );
    } catch {
      return true;
    }
  }

  public get passwordConfig(): PasswordConfig {
    return AppConfigService.PASS_CONFIG;
  }

  public static getInstance() {
    return this.INSTANCE;
  }

  public setCurrentHospital(hospital: Hospital | undefined) {
    this.currentHospital = hospital;
  }

  public getCurrentHospital() {
    return this.currentHospital;
  }

  public phoneVerificationPromptDeclined() {
    this.storageAccess
      ?.getInstance()
      .setItem(AppConfigService.KEY_PHONE_VERIFICATION_PROMPT, `${Date.now()}`);
  }

  public getLink(link: SupportLinkName) {
    return this.currentCountry && supportLinks[this.currentCountry][link];
  }

  protected getAppCode(): ClientApp {
    return ClientApp.PATIENT_APP;
  }

  protected getVersionFromPackageJson(): string {
    return process.env.NEXT_PUBLIC_APP_RUNTIME_VERSION ?? 'N/A';
  }
}
